/**
 * Fibre-G Precision Nutrition - Full-Stack PostgreSQL & Shopify AJAX Cart Engine
 * User-Scoped Database Persistence via Prisma + Native Slide-Out AJAX Drawer
 */
window.FibregCart = (function () {
  'use strict';

  const PENDING_ACTION_KEY = 'fibreg_pending_cart_action';
  const GUEST_CART_KEY = 'fibreg_guest_cart_items';
  const MAX_PENDING_AGE_MS = 2 * 60 * 60 * 1000; // 2 hours

  const SELECTORS = {
    cartDrawer: '#cart-drawer',
    cartOverlay: '#cart-drawer-overlay',
    cartCountPills: '[data-cart-count]',
    cartSubtotal: '[data-cart-subtotal]',
    cartItemsContainer: '[data-cart-items]',
    cartDrawerClose: '[data-cart-drawer-close]',
    cartTriggers: '[data-cart-trigger]',
    addToCartForms: 'form[action*="/cart/add"], .product-form, #product-form',
  };

  function getApiUrl() {
    if (window.FibregAuth && typeof window.FibregAuth.getApiUrl === 'function') {
      return window.FibregAuth.getApiUrl();
    }
    return (window.location.hostname === '127.0.0.1' || window.location.hostname === 'localhost')
      ? 'http://localhost:3001'
      : 'https://fibre-g-server-production.up.railway.app';
  }

  function isUserAuthenticated() {
    if (window.FibregAuth && typeof window.FibregAuth.isAuthenticated === 'function') {
      return window.FibregAuth.isAuthenticated();
    }
    try {
      return !!(localStorage.getItem('fibreg_auth_token') || localStorage.getItem('fibreg_auth_user') || sessionStorage.getItem('fibreg_verified_customer'));
    } catch (e) {
      return false;
    }
  }

  function getAuthToken() {
    try {
      if (window.FibregAuth && typeof window.FibregAuth.getToken === 'function') {
        const t = window.FibregAuth.getToken();
        if (t) return t;
      }
      return localStorage.getItem('fibreg_auth_token') || null;
    } catch (e) {
      return null;
    }
  }

  function formatMoney(amountInRupees) {
    let rupees = typeof amountInRupees === 'number' ? amountInRupees : parseFloat(amountInRupees) || 0;
    return '₹' + rupees.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  // --- Guest Local Cart Management ---
  function getGuestCart() {
    try {
      const raw = localStorage.getItem(GUEST_CART_KEY);
      const items = raw ? JSON.parse(raw) : [];
      const totalQuantity = items.reduce((s, i) => s + (i.quantity || 1), 0);
      const totalPrice = items.reduce((s, i) => s + (i.price || 1499) * (i.quantity || 1), 0);
      return {
        items: items,
        item_count: items.length,
        totalQuantity: totalQuantity,
        totalPrice: totalPrice,
        total_price: Math.round(totalPrice * 100),
      };
    } catch (e) {
      return { items: [], item_count: 0, totalQuantity: 0, totalPrice: 0, total_price: 0 };
    }
  }

  function saveGuestCart(items) {
    try {
      localStorage.setItem(GUEST_CART_KEY, JSON.stringify(items));
    } catch (e) {}
  }

  // --- Pending Cart Action Store ---
  function savePendingAction(variantId, quantity, action, productDetails) {
    if (!variantId && !productDetails) return;
    try {
      const payload = {
        variantId: String(variantId || productDetails?.variantId || 'fibreg-default'),
        quantity: parseInt(quantity, 10) || 1,
        action: action || 'add_to_cart',
        productDetails: productDetails || {},
        createdAt: new Date().toISOString(),
      };
      localStorage.setItem(PENDING_ACTION_KEY, JSON.stringify(payload));
    } catch (e) {
      console.error('[FibregCart] Failed to save pending action:', e);
    }
  }

  function getPendingAction() {
    try {
      const raw = localStorage.getItem(PENDING_ACTION_KEY);
      if (!raw) return null;
      const data = JSON.parse(raw);
      if (!data || !data.createdAt) {
        clearPendingAction();
        return null;
      }
      const age = Date.now() - new Date(data.createdAt).getTime();
      if (age > MAX_PENDING_AGE_MS) {
        clearPendingAction();
        return null;
      }
      return data;
    } catch (e) {
      clearPendingAction();
      return null;
    }
  }

  function clearPendingAction() {
    try {
      localStorage.removeItem(PENDING_ACTION_KEY);
    } catch (e) {}
  }

  // Clear Cart from LocalStorage, PostgreSQL Backend, and Shopify Native Cart
  async function clearCart() {
    const token = getAuthToken();
    clearPendingAction();
    localStorage.removeItem(GUEST_CART_KEY);
    localStorage.removeItem('fibreg_cart_cache');
    try {
      sessionStorage.removeItem('fibreg_in_checkout');
    } catch (e) {}

    // Clear Shopify native session cart
    try {
      await fetch((window.FibregTheme?.routes?.cart_clear_url || '/cart/clear.js'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      });
    } catch (e) {}

    // Clear PostgreSQL Database cart if authenticated
    if (token) {
      try {
        await fetch(getApiUrl() + '/api/cart/clear', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + token,
          },
        });
      } catch (e) {
        console.warn('[FibregCart] DB cart clear error:', e);
      }
    }

    renderDrawer({ items: [], item_count: 0, totalQuantity: 0, totalPrice: 0, total_price: 0 });
  }

  // Check if Order was placed or user is on Thank You / Order Confirmation page
  async function checkOrderCompletion() {
    const path = (window.location.pathname || '').toLowerCase();
    const search = (window.location.search || '').toLowerCase();
    const isThankYouPage = path.includes('/thank_you') ||
      path.includes('/orders/') ||
      path.includes('/order-status') ||
      search.includes('order_status') ||
      (window.Shopify && window.Shopify.Checkout && window.Shopify.Checkout.isOrderStatusPage);

    if (isThankYouPage) {
      console.log('🎉 [FibregCart] Order completion detected. Clearing cart.');
      await clearCart();
      return true;
    }

    // If user returned from checkout and Shopify's cart is now 0 (order was completed)
    let wasInCheckout = false;
    try {
      wasInCheckout = sessionStorage.getItem('fibreg_in_checkout') === 'true';
    } catch (e) {}

    if (wasInCheckout) {
      try {
        sessionStorage.removeItem('fibreg_in_checkout');
        const res = await fetch('/cart.js');
        if (res.ok) {
          const shopifyCart = await res.json();
          if (shopifyCart.item_count === 0) {
            console.log('🎉 [FibregCart] Checkout completed into order. Clearing database cart.');
            await clearCart();
            return true;
          }
        }
      } catch (e) {}
    }

    return false;
  }

  // Sync Guest Cart & Pending Actions to PostgreSQL after Login
  async function syncCartAfterLogin() {
    const token = getAuthToken();
    if (!token) return;

    const pending = getPendingAction();
    const guestCart = getGuestCart();

    // 1. If there was a pending item from before login
    if (pending && pending.productDetails) {
      clearPendingAction();
      localStorage.removeItem(GUEST_CART_KEY);

      if (pending.action === 'buy_now') {
        await buyNow(pending.variantId, pending.quantity, pending.productDetails);
        return;
      }

      try {
        const updatedCart = await addItem(
          pending.variantId,
          pending.quantity,
          pending.productDetails
        );

        if (pending.action === 'checkout') {
          await proceedToCheckout();
          return;
        }

        if (updatedCart) {
          renderDrawer(updatedCart);
          openDrawer();
          return;
        }
      } catch (e) {
        console.error('[FibregCart] Sync pending action error:', e);
      }
    } else if (guestCart.items && guestCart.items.length > 0) {
      // 2. If guest cart had items, sync them to PostgreSQL
      try {
        const res = await fetch(getApiUrl() + '/api/cart/sync', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + token,
          },
          body: JSON.stringify({ items: guestCart.items }),
        });
        localStorage.removeItem(GUEST_CART_KEY);
        if (res.ok) {
          const updatedCart = await res.json();
          renderDrawer(updatedCart);
          openDrawer();
          return;
        }
      } catch (err) {
        console.warn('[FibregCart] Error syncing guest cart on login:', err);
      }
    }

    const cart = await getCart();
    renderDrawer(cart);
  }

  // Fetch Cart State (From PostgreSQL Database if authenticated)
  async function getCart() {
    const isAuthed = window.FibregAuth && window.FibregAuth.isAuthenticated();
    const token = getAuthToken();

    if (isAuthed && token) {
      try {
        const res = await fetch(getApiUrl() + '/api/cart', {
          method: 'GET',
          headers: {
            Authorization: 'Bearer ' + token,
          },
        });

        if (res.ok) {
          const dbCart = await res.json();
          return dbCart;
        }
      } catch (err) {
        console.warn('[FibregCart] Database cart fetch offline:', err);
      }
    }

    // For unauthenticated guest users: cart is empty before login
    return { items: [], item_count: 0, totalQuantity: 0, totalPrice: 0, total_price: 0 };
  }

  // Open Drawer (Guarded by user authentication)
  function openDrawer() {
    if (!isUserAuthenticated()) {
      if (window.FibregAuth && typeof window.FibregAuth.openVerificationModal === 'function') {
        window.FibregAuth.openVerificationModal();
      }
      return;
    }
    const drawer = document.querySelector(SELECTORS.cartDrawer);
    const overlay = document.querySelector(SELECTORS.cartOverlay);
    if (drawer && overlay) {
      drawer.classList.add('active');
      overlay.classList.add('active');
      document.body.style.overflow = 'hidden';
    }
  }

  // Close Drawer
  function closeDrawer() {
    const drawer = document.querySelector(SELECTORS.cartDrawer);
    const overlay = document.querySelector(SELECTORS.cartOverlay);
    if (drawer && overlay) {
      drawer.classList.remove('active');
      overlay.classList.remove('active');
      document.body.style.overflow = '';
    }
  }

  // Update Cart Count Badges
  function updateCartCount(itemCount) {
    const count = parseInt(itemCount, 10) || 0;
    document.querySelectorAll(SELECTORS.cartCountPills).forEach((el) => {
      el.textContent = count;
      if (count > 0) {
        el.classList.remove('hidden');
        el.style.display = 'inline-flex';
      } else {
        el.classList.add('hidden');
        el.style.display = 'none';
      }
    });
  }

  // Render Cart Drawer HTML
  function renderDrawer(cart) {
    if (!cart) return;

    const itemCount = cart.items ? cart.items.length : (cart.item_count !== undefined ? cart.item_count : 0);
    updateCartCount(itemCount);

    const subtotalEl = document.querySelector(SELECTORS.cartSubtotal);
    if (subtotalEl) {
      const priceVal = cart.totalPrice !== undefined ? cart.totalPrice : (cart.total_price ? cart.total_price / 100 : 0);
      subtotalEl.textContent = formatMoney(priceVal);
    }

    const itemsContainer = document.querySelector(SELECTORS.cartItemsContainer);
    if (!itemsContainer) return;

    const footerEl = document.querySelector('.cart-drawer-footer, [data-cart-footer]');

    if (!cart.items || cart.items.length === 0) {
      if (footerEl) {
        footerEl.classList.add('hidden');
        footerEl.style.display = 'none';
      }
      const emptyCartImg = window.FibregTheme?.emptyCartUrl || '';
      itemsContainer.innerHTML = `
        <div style="text-align: center; padding: 2.5rem 1rem; color: #6b7280; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 0.875rem; margin: auto 0;">
          ${emptyCartImg ? `
            <div style="width: 11rem; max-width: 100%; height: auto; display: flex; align-items: center; justify-content: center;">
              <img src="${emptyCartImg}" alt="Your cart is empty" style="width: 100%; height: auto; object-fit: contain; max-height: 170px;">
            </div>
          ` : `
            <div style="width: 3.5rem; height: 3.5rem; border-radius: 1rem; background: #f4f4f5; display: flex; align-items: center; justify-content: center; font-size: 1.5rem;">🛒</div>
          `}
          <div style="display: flex; flex-direction: column; gap: 0.25rem;">
            <p style="font-family: var(--font-heading); font-weight: 800; font-size: 1.125rem; color: #111827; margin: 0;">Your cart is empty</p>
            <p style="font-size: 0.75rem; margin: 0; color: #6b7280; max-width: 220px;">Add items to get started</p>
          </div>
          <a href="/pages/shop" class="btn btn-primary" style="margin-top: 0.25rem; padding: 0.625rem 1.5rem; background: #006941; color: #fff; border-radius: 0.75rem; font-weight: 700; font-size: 0.8125rem; text-decoration: none; cursor: pointer; display: inline-flex; align-items: center; justify-content: center;" onclick="if(window.FibregCart) window.FibregCart.closeDrawer();">Explore Shop</a>
        </div>
      `;
      return;
    }

    if (footerEl) {
      footerEl.classList.remove('hidden');
      footerEl.style.display = 'flex';
    }

    const html = cart.items.map((item) => {
      const itemKey = item.variantId || item.id || item.key;
      const title = item.title || item.product_title || 'Fibre-G Precision Bio-Serving';
      const variantTitle = item.variantTitle || item.variant_title || '';
      const price = item.price || (item.final_line_price ? item.final_line_price / 100 : 1499);
      const quantity = item.quantity || 1;
      const lineTotal = price * quantity;
      const imageSrc = item.image || (item.featured_image ? item.featured_image.url : '') || '';

      return `
        <div class="cart-drawer-item p-3.5 bg-white rounded-2xl border border-[#e5e7eb] flex gap-3.5 items-start relative" data-item-key="${itemKey}" style="border: 1px solid #e5e7eb; border-radius: 16px; padding: 14px; display: flex; gap: 14px; background: #ffffff;">
          <div style="width: 4.5rem; height: 4.5rem; border-radius: 0.75rem; background: #f4f4f5; border: 1px solid #ebebeb; overflow: hidden; flex-shrink: 0; display: flex; align-items: center; justify-content: center; padding: 0.375rem;">
            ${imageSrc ? `<img src="${imageSrc}" alt="${title}" style="width: 100%; height: 100%; object-fit: contain;">` : `<span style="font-size: 1.5rem;">🌱</span>`}
          </div>
          <div style="flex: 1; min-width: 0; display: flex; flex-direction: column; justify-content: space-between; align-self: stretch; padding: 0.125rem 0;">
            <div style="display: flex; align-items: flex-start; justify-content: space-between; gap: 0.25rem;">
              <div style="display: flex; flex-direction: column; min-width: 0; padding-right: 0.25rem;">
                <h4 style="font-family: var(--font-heading); font-weight: 700; font-size: 0.875rem; color: #111827; margin: 0; line-height: 1.2; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">
                  ${title}
                </h4>
                ${variantTitle ? `<span style="font-size: 0.6875rem; color: #6b7280; margin-top: 0.125rem;">${variantTitle}</span>` : ''}
              </div>
              <button
                type="button"
                style="color: #ef4444; border: none; background: transparent; cursor: pointer; padding: 0.25rem; border-radius: 0.375rem; display: flex; align-items: center; justify-content: center; flex-shrink: 0;"
                onclick="FibregCart.changeQuantity('${itemKey}', 0)"
                title="Remove item"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M3 6h18"/><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/><line x1="10" x2="10" y1="11" y2="17"/><line x1="14" x2="14" y1="11" x2="14" y2="17"/>
                </svg>
              </button>
            </div>
            <div style="display: flex; align-items: center; justify-content: space-between; margin-top: 0.625rem;">
              <span style="font-family: var(--font-heading); font-weight: 900; font-size: 0.875rem; color: #111827;">
                ${formatMoney(lineTotal)}
              </span>
              <div style="display: flex; align-items: center; background: #f0f0f0; border-radius: 9999px; padding: 0.125rem 0.625rem; gap: 0.5rem;">
                <button type="button" style="font-size: 0.75rem; font-weight: bold; color: #111827; background: transparent; border: none; cursor: pointer; padding: 0.125rem;" onclick="FibregCart.changeQuantity('${itemKey}', ${quantity - 1})">−</button>
                <span style="font-family: var(--font-heading); font-weight: 700; font-size: 0.75rem; min-width: 0.875rem; text-align: center; color: #111827;">${quantity}</span>
                <button type="button" style="font-size: 0.75rem; font-weight: bold; color: #111827; background: transparent; border: none; cursor: pointer; padding: 0.125rem;" onclick="FibregCart.changeQuantity('${itemKey}', ${quantity + 1})">+</button>
              </div>
            </div>
          </div>
        </div>
      `;
    }).join('');

    itemsContainer.innerHTML = html;
  }

  // Public Add Item to Cart (Guarded by customer authentication)
  async function addItem(variantId, quantity = 1, details = {}) {
    // Auth Check: User must be logged in to add to cart
    if (!isUserAuthenticated()) {
      savePendingAction(variantId, quantity, 'add_to_cart', details);
      if (window.FibregAuth && typeof window.FibregAuth.openVerificationModal === 'function') {
        window.FibregAuth.openVerificationModal({
          onVerified: function () {
            syncCartAfterLogin();
          },
        });
      }
      return false;
    }

    const normalizedVariantId = String(variantId || details.variantId || '48534724968585');
    const title = details.title || 'Fibre-G';
    const variantTitle = details.variantTitle || '150 g';
    const price = parseFloat(details.price) || 199;
    const qty = parseInt(quantity, 10) || 1;
    const image = details.image || '';

    const payload = {
      variantId: normalizedVariantId,
      title: title,
      variantTitle: variantTitle,
      price: price,
      quantity: qty,
      image: image,
    };

    // 1. Sync to Shopify's native session cart for checkout
    const cleanNumericVariantId = parseInt(normalizedVariantId, 10) || (window.FibregTheme?.defaultVariantId ? parseInt(window.FibregTheme.defaultVariantId, 10) : 48534724968585);
    if (cleanNumericVariantId && !isNaN(cleanNumericVariantId)) {
      try {
        await fetch((window.FibregTheme?.routes?.cart_add_url || '/cart/add.js'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
          },
          body: JSON.stringify({
            items: [{
              id: cleanNumericVariantId,
              quantity: qty,
            }],
          }),
        });
      } catch (sErr) {
        console.warn('[FibregCart] Shopify native cart add notice:', sErr);
      }
    }

    // 2. Update local guest cart preserving all existing items
    const guestCart = getGuestCart();
    const existingIdx = guestCart.items.findIndex((i) => String(i.variantId) === String(normalizedVariantId));
    if (existingIdx > -1) {
      guestCart.items[existingIdx].quantity += qty;
    } else {
      guestCart.items.push(payload);
    }
    saveGuestCart(guestCart.items);

    const updatedCart = getGuestCart();
    renderDrawer(updatedCart);
    openDrawer();
    return updatedCart;
  }

  // Change Quantity of an Item (Updates Shopify Native Cart and Guest Cart)
  async function changeQuantity(key, quantity) {
    const targetQty = Math.max(0, parseInt(quantity, 10) || 0);
    const cleanNumericKey = parseInt(key, 10) || (window.FibregTheme?.defaultVariantId ? parseInt(window.FibregTheme.defaultVariantId, 10) : null);

    if (cleanNumericKey && !isNaN(cleanNumericKey)) {
      try {
        await fetch((window.FibregTheme?.routes?.cart_change_url || '/cart/change.js'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
          },
          body: JSON.stringify({
            id: String(cleanNumericKey),
            quantity: targetQty,
          }),
        });
      } catch (sErr) {
        console.warn('[FibregCart] Shopify native cart change notice:', sErr);
      }
    }

    // Update local cart
    const guestCart = getGuestCart();
    if (targetQty <= 0) {
      guestCart.items = guestCart.items.filter((i) => (i.variantId !== key && i.id !== key));
    } else {
      const item = guestCart.items.find((i) => (i.variantId === key || i.id === key));
      if (item) {
        item.quantity = targetQty;
      }
    }

    saveGuestCart(guestCart.items);
    renderDrawer(getGuestCart());
  }

  // Public Buy Now (Guarded by customer authentication)
  async function buyNow(variantId, quantity = 1, details = {}) {
    // Auth Check: User must be logged in to purchase
    if (!isUserAuthenticated()) {
      savePendingAction(variantId, quantity, 'buy_now', details);
      if (window.FibregAuth && typeof window.FibregAuth.openVerificationModal === 'function') {
        window.FibregAuth.openVerificationModal({
          onVerified: function () {
            syncCartAfterLogin();
          },
        });
      }
      return false;
    }

    const normalizedVariantId = String(variantId || details.variantId || '48534724968585');
    const qty = Math.max(1, parseInt(quantity, 10) || 1);
    const payload = {
      variantId: normalizedVariantId,
      title: details.title || 'Fibre-G',
      variantTitle: details.variantTitle || '150 g',
      price: parseFloat(details.price) || 199,
      quantity: qty,
      image: details.image || '',
    };

    const cleanNumericVariantId = parseInt(normalizedVariantId, 10) || (window.FibregTheme?.defaultVariantId ? parseInt(window.FibregTheme.defaultVariantId, 10) : 48534724968585);

    // Track checkout state
    try {
      sessionStorage.setItem('fibreg_in_checkout', 'true');
    } catch (e) {}

    // Add item to Shopify native cart WITHOUT clearing unrelated items
    try {
      await fetch((window.FibregTheme?.routes?.cart_add_url || '/cart/add.js'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items: [{ id: cleanNumericVariantId, quantity: qty }] }),
      });
    } catch (e) {
      console.error('[FibregCart] Buy Now sync error:', e);
    }

    // Update local cart preserving existing items
    const guestCart = getGuestCart();
    const existingIdx = guestCart.items.findIndex((i) => String(i.variantId) === String(normalizedVariantId));
    if (existingIdx > -1) {
      guestCart.items[existingIdx].quantity += qty;
    } else {
      guestCart.items.push(payload);
    }
    saveGuestCart(guestCart.items);

    await proceedToCheckout();
  }

  // Dedicated, Unified Checkout Function for Drawer and Cart Page
  async function proceedToCheckout(triggerBtn) {
    // Auth Check: User must be logged in to proceed to checkout
    if (!isUserAuthenticated()) {
      savePendingAction(null, null, 'checkout', null);
      if (window.FibregAuth && typeof window.FibregAuth.openVerificationModal === 'function') {
        window.FibregAuth.openVerificationModal({
          onVerified: function () {
            proceedToCheckout(triggerBtn);
          },
        });
      }
      return false;
    }

    const guestCart = getGuestCart();
    const activeItems = (guestCart.items && guestCart.items.length > 0)
      ? guestCart.items
      : [];

    if (!activeItems || activeItems.length === 0) {
      openDrawer();
      return;
    }


    // Update button state to indicate processing
    const btn = triggerBtn || document.querySelector('button[name="checkout"], [data-checkout-btn]');
    const originalText = btn ? btn.innerHTML : '';
    if (btn) {
      btn.disabled = true;
      btn.innerHTML = '<span>Proceeding to Checkout...</span>';
    }

    // Track checkout state so when user completes order, returning to store clears the cart
    try {
      sessionStorage.setItem('fibreg_in_checkout', 'true');
    } catch (e) {}

    try {
      // Ensure Shopify session cart (/cart.js) has all line items with exact variants and quantities
      const shopifyItems = activeItems.map((item) => {
        const vid = parseInt(item.variantId || item.id, 10) || (window.FibregTheme?.defaultVariantId ? parseInt(window.FibregTheme.defaultVariantId, 10) : 48534724968585);
        return {
          id: vid,
          quantity: parseInt(item.quantity, 10) || 1,
        };
      }).filter((i) => i.id && !isNaN(i.id));

      if (shopifyItems.length > 0) {
        await fetch((window.FibregTheme?.routes?.cart_clear_url || '/cart/clear.js'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        });

        await fetch((window.FibregTheme?.routes?.cart_add_url || '/cart/add.js'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ items: shopifyItems }),
        });
      }

      // Attach verified customer attributes to Shopify Cart so they persist with the order
      const verifiedCustomer = window.FibregAuth && typeof window.FibregAuth.getVerifiedCustomer === 'function'
        ? window.FibregAuth.getVerifiedCustomer()
        : null;

      if (verifiedCustomer && verifiedCustomer.email) {
        try {
          await fetch((window.FibregTheme?.routes?.cart_update_url || '/cart/update.js'), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({
              attributes: {
                'Customer Name': verifiedCustomer.name || '',
                'Delivery Phone': verifiedCustomer.mobile || '',
                'Verified Email': verifiedCustomer.email || '',
                'Email Verification Token': verifiedCustomer.token || '',
                'Email Verified At': verifiedCustomer.verifiedAt || new Date().toISOString(),
              },
            }),
          });
        } catch (attrErr) {
          console.warn('[FibregCart] Could not attach verified attributes to cart:', attrErr);
        }
      }

      // Navigate directly to Shopify native checkout URL, preserving authenticated or verified customer email
      let checkoutUrl = window.FibregTheme?.routes?.checkout_url || (window.FibregTheme?.rootUrl ? `${window.FibregTheme.rootUrl.replace(/\/$/, '')}/checkout` : '/checkout');
      const customerEmail = (verifiedCustomer && verifiedCustomer.email) || window.FibregShopifyCustomer?.email || (window.FibregAuth && window.FibregAuth.getUser()?.email);
      if (customerEmail && !checkoutUrl.includes('checkout[email]')) {
        const sep = checkoutUrl.includes('?') ? '&' : '?';
        checkoutUrl += `${sep}checkout[email]=${encodeURIComponent(customerEmail)}`;
      }
      window.location.href = checkoutUrl;
    } catch (err) {
      console.error('[FibregCart] Checkout error:', err);
      let checkoutUrl = window.FibregTheme?.routes?.checkout_url || '/checkout';
      const customerEmail = (window.FibregAuth && window.FibregAuth.getVerifiedCustomer()?.email) || window.FibregShopifyCustomer?.email || (window.FibregAuth && window.FibregAuth.getUser()?.email);
      if (customerEmail && !checkoutUrl.includes('checkout[email]')) {
        const sep = checkoutUrl.includes('?') ? '&' : '?';
        checkoutUrl += `${sep}checkout[email]=${encodeURIComponent(customerEmail)}`;
      }
      window.location.href = checkoutUrl;
    } finally {
      if (btn) {
        btn.disabled = false;
        btn.innerHTML = originalText;
      }
    }
  }

  // Global Click & Submit Interceptors
  function initInterceptors() {
    // 0. CAPTURE PHASE: Gate all buy now, add to cart, and checkout actions when not authenticated
    document.addEventListener('click', (e) => {
      // (a) Intercept Buy Now buttons
      const buyBtn = e.target.closest('[data-buy-now], .btn-buy-now, #shop-buy-now-btn, [name="buy"], .shopify-payment-button__button');
      if (buyBtn && !isUserAuthenticated()) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();

        const container = buyBtn.closest('.product-card, .main-product-section, .shopify-section, section') || document;
        const variantId = buyBtn.getAttribute('data-variant-id') || buyBtn.getAttribute('data-id') || (window.FibregTheme?.defaultVariantId || '48534724968585');
        const qty = parseInt(buyBtn.getAttribute('data-quantity') || document.getElementById('qty-display')?.textContent.trim() || '1', 10) || 1;
        const title = buyBtn.getAttribute('data-title') || container.querySelector('h1, h2, h3, [data-product-title]')?.textContent.trim() || 'Fibre-G';
        const price = parseFloat(buyBtn.getAttribute('data-price')) || parseFloat(container.querySelector('.price, [data-price]')?.textContent.replace(/[^\d.]/g, '')) || 199;
        const imgEl = container.querySelector('img');
        const image = buyBtn.getAttribute('data-image') || (imgEl ? imgEl.src : '');

        savePendingAction(variantId, qty, 'buy_now', {
          variantId: String(variantId),
          title: title,
          price: price,
          image: image,
        });

        if (window.FibregAuth && typeof window.FibregAuth.openVerificationModal === 'function') {
          window.FibregAuth.openVerificationModal({
            onVerified: function () {
              syncCartAfterLogin();
            },
          });
        }
        return;
      }

      // (b) Intercept Add to Cart buttons
      const addBtn = e.target.closest('[data-add-to-cart], .btn-add-to-cart, #shop-add-to-cart-btn, [name="add"]');
      if (addBtn && !isUserAuthenticated()) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();

        const container = addBtn.closest('.product-card, .main-product-section, .shopify-section, section') || document;
        const variantId = addBtn.getAttribute('data-variant-id') || addBtn.getAttribute('data-id') || (window.FibregTheme?.defaultVariantId || '48534724968585');
        const qty = parseInt(addBtn.getAttribute('data-quantity') || document.getElementById('qty-display')?.textContent.trim() || '1', 10) || 1;
        const title = addBtn.getAttribute('data-title') || container.querySelector('h1, h2, h3, [data-product-title]')?.textContent.trim() || 'Fibre-G';
        const price = parseFloat(addBtn.getAttribute('data-price')) || parseFloat(container.querySelector('.price, [data-price]')?.textContent.replace(/[^\d.]/g, '')) || 199;
        const imgEl = container.querySelector('img');
        const image = addBtn.getAttribute('data-image') || (imgEl ? imgEl.src : '');

        savePendingAction(variantId, qty, 'add_to_cart', {
          variantId: String(variantId),
          title: title,
          price: price,
          image: image,
        });

        if (window.FibregAuth && typeof window.FibregAuth.openVerificationModal === 'function') {
          window.FibregAuth.openVerificationModal({
            onVerified: function () {
              syncCartAfterLogin();
            },
          });
        }
        return;
      }

      // (c) Intercept Cart trigger buttons when unauthenticated
      const cartBtn = e.target.closest('[data-cart-trigger], [data-auth-cart-control]');
      if (cartBtn && !isUserAuthenticated()) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();

        if (window.FibregAuth && typeof window.FibregAuth.openVerificationModal === 'function') {
          window.FibregAuth.openVerificationModal();
        }
        return;
      }
    }, true);

    // 1. Intercept Add to Cart form submissions
    document.addEventListener('submit', async (e) => {
      const form = e.target.closest('form[action*="/cart/add"], .product-form, #product-form');
      if (!form) return;

      e.preventDefault();
      const submitBtn = form.querySelector('[type="submit"]');
      const originalText = submitBtn ? submitBtn.innerHTML : '';

      try {
        const formData = new FormData(form);
        const variantId = formData.get('id') || form.querySelector('[name="id"]')?.value || '48534724968585';
        const quantity = parseInt(formData.get('quantity') || form.querySelector('[name="quantity"]')?.value || '1', 10);
        
        // Contextual card or container search
        const container = form.closest('.product-card, .main-product-section, .shopify-section, section') || document;
        const titleEl = container.querySelector('h1, h2, h3, [data-product-title]');
        const title = titleEl ? titleEl.textContent.trim() : 'Fibre-G Precision Bio-Serving';
        
        const priceEl = container.querySelector('.price, [data-price], [data-product-price]');
        const priceText = priceEl ? priceEl.textContent.replace(/[^\d.]/g, '') : '199';
        const price = parseFloat(priceText) || 199;

        const imgEl = container.querySelector('img');
        const image = imgEl ? imgEl.src : '';

        if (!isUserAuthenticated()) {
          savePendingAction(variantId, quantity, 'add_to_cart', {
            variantId: String(variantId),
            title: title,
            price: price,
            image: image,
          });
          if (window.FibregAuth && typeof window.FibregAuth.openVerificationModal === 'function') {
            window.FibregAuth.openVerificationModal({
              onVerified: function () {
                syncCartAfterLogin();
              },
            });
          }
          return;
        }

        if (submitBtn) {
          submitBtn.disabled = true;
          submitBtn.innerHTML = '<span>Adding...</span>';
        }

        await addItem(variantId, quantity, {
          variantId: String(variantId),
          title: title,
          price: price,
          image: image,
        });
      } catch (err) {
        console.error('[FibregCart] Form submission error:', err);
      } finally {
        if (submitBtn) {
          submitBtn.disabled = false;
          submitBtn.innerHTML = originalText;
        }
      }
    });

    // 2. Intercept Add to Cart button clicks
    document.addEventListener('click', async (e) => {
      const btn = e.target.closest('[data-add-to-cart], .btn-add-to-cart, [name="add"]');
      if (!btn) return;

      // If button is inside a form, the form submit handler handles it
      if (btn.closest('form')) return;

      e.preventDefault();
      const container = btn.closest('.product-card, .main-product-section, section') || document;
      const variantId = btn.getAttribute('data-variant-id') || btn.getAttribute('data-id') || (window.FibregTheme?.defaultVariantId || '48534724968585');
      const qty = parseInt(btn.getAttribute('data-quantity') || '1', 10);
      const title = btn.getAttribute('data-title') || container.querySelector('h1, h2, h3')?.textContent.trim() || 'Fibre-G';
      const price = parseFloat(btn.getAttribute('data-price')) || parseFloat(container.querySelector('.price, [data-price]')?.textContent.replace(/[^\d.]/g, '')) || 199;
      const imgEl = container.querySelector('img');
      const image = btn.getAttribute('data-image') || (imgEl ? imgEl.src : '');

      const originalText = btn.innerHTML;
      btn.disabled = true;
      btn.innerHTML = '<span>Adding...</span>';
      try {
        await addItem(variantId, qty, {
          variantId: String(variantId),
          title: title,
          price: price,
          image: image,
        });
      } catch (err) {
        console.error('[FibregCart] Button click error:', err);
      } finally {
        btn.disabled = false;
        btn.innerHTML = originalText;
      }
    });

    // 3. Intercept Checkout button clicks & form submissions SYNCHRONOUSLY
    document.addEventListener('click', (e) => {
      const checkoutBtn = e.target.closest('button[name="checkout"], [data-checkout-btn], .btn-checkout, a[href*="/checkout"]');
      if (checkoutBtn) {
        e.preventDefault();
        e.stopPropagation();
        proceedToCheckout(checkoutBtn);
      }
    });

    document.addEventListener('submit', (e) => {
      const form = e.target.closest('form[action*="/cart"], #cart-form, #cart-drawer-form');
      if (form && !form.matches('form[action*="/cart/add"], .product-form, #product-form')) {
        e.preventDefault();
        e.stopPropagation();
        proceedToCheckout(form.querySelector('button[name="checkout"], [type="submit"]'));
      }
    });
  }

  // Initialize
  async function init() {
    initInterceptors();

    // Check if order was placed, if so clear cart
    const orderCompleted = await checkOrderCompletion();
    if (orderCompleted) {
      return;
    }

    // Listen for auth events to load user cart & execute sync
    window.addEventListener('fibreg:auth:change', (e) => {
      if (e.detail && e.detail.isAuthenticated) {
        syncCartAfterLogin();
      } else {
        renderDrawer(getGuestCart());
      }
    });

    // Drawer open triggers
    document.querySelectorAll(SELECTORS.cartTriggers).forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        if (!isUserAuthenticated()) {
          if (window.FibregAuth && typeof window.FibregAuth.openVerificationModal === 'function') {
            window.FibregAuth.openVerificationModal();
          }
          return;
        }
        getCart().then((cart) => {
          renderDrawer(cart);
          openDrawer();
        });
      });
    });

    // Drawer close triggers
    document.querySelectorAll(SELECTORS.cartDrawerClose).forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        closeDrawer();
      });
    });

    const overlay = document.querySelector(SELECTORS.cartOverlay);
    if (overlay) {
      overlay.addEventListener('click', closeDrawer);
    }

    // Initial cart load
    getCart().then((cart) => {
      if (cart) {
        renderDrawer(cart);
        if (window.location.search.includes('open_cart=1')) {
          openDrawer();
          try {
            const cleanUrl = window.location.pathname + window.location.search.replace(/[?&]open_cart=1/, '').replace(/^&/, '?');
            window.history.replaceState({}, document.title, cleanUrl || window.location.pathname);
          } catch (e) {}
        }
      }
    });
  }

  if (document.readyState !== 'loading') {
    init();
  } else {
    document.addEventListener('DOMContentLoaded', init);
  }
  document.addEventListener('shopify:section:load', init);

  return {
    getCart: getCart,
    addItem: addItem,
    changeQuantity: changeQuantity,
    buyNow: buyNow,
    proceedToCheckout: proceedToCheckout,
    clearCart: clearCart,
    openDrawer: openDrawer,
    closeDrawer: closeDrawer,
    renderDrawer: renderDrawer,
    formatMoney: formatMoney,
    getPendingAction: getPendingAction,
    syncCartAfterLogin: syncCartAfterLogin,
    executePendingAction: syncCartAfterLogin,
  };
})();
